import * as Pioneer from 'pioneer';
import { Cameras, SceneHelpers } from 'pioneer-scripts';
import { refsStore, compareStore, spacecraftStore } from '../managers/globalState';
import ar from '../languages/ar';

class ComparisonManager {
	constructor(pioneer, scene) {
		this._pioneer = pioneer;
		this._scene = scene;
		this._comparisonObject = null;

		/**
		 * Compare obj data with each obj's forward-facing axis
		 * @type {object}
		 * @private
		 */

		this._compareObjData = {
			scientist: {
				forwardAxis: Pioneer.Vector3.YAxisNeg
			},
			school_bus: {
				forwardAxis: Pioneer.Vector3.XAxisNeg
			},
			astronaut: {
				forwardAxis: Pioneer.Vector3.YAxisNeg
			}
			,
			camel: {
				forwardAxis: Pioneer.Vector3.YAxisNeg
			}
		};

		/**
		 * Custom compare obj positions
		 * Vectors with values in km
		 * @type {object}
		 * @private
		 */
		this._customComparePos = {
			sc_iss: new Pioneer.Vector3(0, 0.02, 0),
			sc_oco_2: new Pioneer.Vector3(0, 0.005, 0)
		};

		/**
		 * List of items to compare spacecraft to.
		 * @type {object}
		 * @public
		 */
		this.compareList = [ar.Scientist, ar.School_Bus, ar.Astronaut,ar.Camel,  ar.none];

		this.updateCompareObject = this.updateCompareObject.bind(this);

		spacecraftStore.subscribeAll(({ spacecraftName, isZoomedOut }) => {
			// If anything spacecraft changes (spacecraft, zoom state, etc.), reset comparison object
			this.reset(spacecraftName, isZoomedOut);
		});

		compareStore.subscribeTo('compareObject', compareObject => {
			const { spacecraftName } = spacecraftStore.stateSnapshot;
			// If the dropdown index changes, update comparisons accordingly
			if (compareObject === 'none') {
				// If user selects 'None' from dropdown, reset

				this.reset(spacecraftName);
			} else {
				// If user selects a compare option, set comparison with selection
				this.compare(spacecraftName, compareObject);
			}
		});
	}

	/**
	 * Return this compare list
	 */
	getCompareList() {
		const dropdownListWithIds = this.compareList.map((item, index) => {
			if (typeof item === 'string') {
				return {
					id: index,
					text: item
				};
			}

			return {
				id: item.id ?? index,
				text: item.text
			};
		});
		return dropdownListWithIds;
	}

	/**
	 * Set global state for compare index
	 * @param {number} compareObjIndex - incoming index selected from Dropdown
	 */
	updateCompareObject(compareObjName) {
		const { get } = refsStore.stateSnapshot;

		const compareObjId = get('content').getCustomEntityId(compareObjName);
		compareStore.setGlobalState({ compareObject: compareObjId });
	}

	/**
	 * Remove a comparison, reset camera and dropdown
	 * @param {string|Entity} spacecraft - optional spacecraft, to know if in spacecraft view
	 * @param {boolean} isZoomedOut - if view is zoomed out or not
	 * @param {boolean} comparing - if in compare mode
	 */
	reset(spacecraft = null, isZoomedOut = false, comparing = false) {
		const { get } = refsStore.stateSnapshot;
		const { compareObject } = compareStore.stateSnapshot;
		if (this._comparisonObject !== null) {
			this._comparisonObject.removeController('compare');

			// Reset camera
			if (!compareObject && spacecraft) {
				get('camera').goToEntity(spacecraft);
			}

			this._comparisonObject.setEnabled(false);
			this._comparisonObject = null;
		}

		if (!comparing) {
			// Reset compare index only if not actively comparing objects
			compareStore.setGlobalState({ compareObject: 'none' });
		}
	}

	/**
	 * Compare object spacecraft with object comparisonObj
	 * @param {string|Entity} spacecraftId - the id of the target object being compared (usually a spacecraft)
	 * @param {string|Entity} comparisonObjIName - the name of the comparison object
	 */
	compare(spacecraftId, comparisonObjName) {
		this.reset(spacecraftId || null, false, true);

		if (!spacecraftId || !comparisonObjName) {
			return;
		}

		let spacecraft;
		if (typeof spacecraftId === 'string') {
			spacecraft = this._scene.get(spacecraftId);
		} else {
			spacecraft = spacecraftId;
		}
		if (typeof comparisonObjName === 'string') {
			this._comparisonObject = this._scene.get(comparisonObjName);
		} else {
			this._comparisonObject = comparisonObjName;
		}

		this._comparisonObject.addParentingTableEntry(0, spacecraft.getName());

		const scRadius = spacecraft.getExtentsRadius();
		const compareObjRadius = this._comparisonObject.getExtentsRadius();

		this._calcComparePosition(spacecraft, scRadius, compareObjRadius);
	}

	/**
	 * Calculate position of the compare object
	 * @param {Entity} spacecraft
	 * @param {number} scRadius - Radius of spacecraft
	 * @param {number} compareObjRadius - Radius of compare object
	 */
	_calcComparePosition(spacecraft, scRadius, compareObjRadius) {
		// Compare object's position and distance multiplier
		const comparePos = new Pioneer.Vector3();
		let distanceMult = 0;

		// Since s/c are orbiters, use s/c position as 'up'
		const up = new Pioneer.Vector3();
		up.copy(spacecraft.getPosition());

		let compareDist = 0;
		if (spacecraft in this._customComparePos) {
			// Custom compare object position
			comparePos.copy(this._customComparePos[spacecraft]);
			compareDist = comparePos.magnitude();
		} else {
			// Calculate default compare object position to be on the left of the s/c
			comparePos.cross(up, spacecraft.getVelocity());

			// Convert from J2000 frame to s/c frame, rotate compare position into s/c orientation
			comparePos.rotateInverse(spacecraft.getOrientation(), comparePos);

			distanceMult = 1.2;
			compareDist = (compareObjRadius + scRadius) * distanceMult;
		}

		// Set compare obj distance from s/c
		comparePos.setMagnitude(comparePos,	compareDist);

		this._setCompareController(spacecraft, comparePos);
	}

	/**
	 * Set compare controllers with calculated values
	 * @param {Entity} spacecraft
	 * @param {object} comparePos - Custom position of compare object
	 */
	async _setCompareController(spacecraft, comparePos) {
		const camera = this._scene.get('camera');

		// Reset controllers
		this._comparisonObject.clearControllers();

		// Set compare controller's properties
		const compareController = this._comparisonObject.addController('fixed', 'compare');
		compareController.setPosition(comparePos);
		compareController.getEntity().setParent(spacecraft);

		// Align compare obj so it 'flies' in the same direction as the spacecraft
		const align = this._comparisonObject.addController('align');

		// OCO-2 'sashays' in its orbit, set custom align type for compare object
		if (spacecraft.getName() === 'sc_oco_2') {
			align.setPrimaryAlignType('align');
		} else {
			align.setPrimaryAlignType('velocity');
		}
		align.setPrimaryAxis(this._compareObjData[this._comparisonObject].forwardAxis);
		align.setPrimaryTargetEntity(spacecraft.getName());

		// Align compare object to always be 'upright'
		align.setSecondaryAlignType('position');
		align.setSecondaryAxis(Pioneer.Vector3.ZAxis);
		align.setSecondaryTargetEntity(spacecraft.getName());

		const rotate = this._comparisonObject.addController('rotateByEntityOrientation');
		rotate.setEntityForOrientation(spacecraft.getName());
		rotate.setRotatingPosition(true);
		// Set it to not use spacecraft orientation
		rotate.setRotatingOrientation(false);

		this._comparisonObject.setEnabled(true);

		await SceneHelpers.waitTillEntitiesInPlace(this._scene, [spacecraft.getName(), this._comparisonObject.getName()]);

		const distance = Cameras.getDistanceToFitEntities(camera, camera.getOrientation(), spacecraft, [this._comparisonObject]) * 1.75;

		await Cameras.goToEntity(camera, spacecraft, {
			distance: distance,
			fixedToParent: true,
			orbiter: true,
			up: true,
			zoom: true
		});

		camera.getControllerByClass(Pioneer.OrbitController).setPitchAngleLimits(new Pioneer.Interval(-Math.PI / 2 + 0.01, Math.PI / 2 - 0.01));

		const zoomController = this._scene.get('camera', 'zoom');
		const minZoom = spacecraft.getOcclusionRadius() * 1.5;
		const maxZoom = (spacecraft.getExtentsRadius() + this._comparisonObject?.getExtentsRadius()) * 20;
		zoomController.setDistanceClamp(new Pioneer.Interval(minZoom, maxZoom));
	}
}

export default ComparisonManager;
